'use client';

import React, { useState } from 'react';
import clsx from 'clsx';
import { LogoCardImageProps } from '@/components/card/types';
import { Link } from '@/components/link/Link';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import '../style.css';
import { Button } from '@/components/button/Button';
import { renameDomProps } from '@/utils/sanitizeDomProps';
import { BaseImage } from '@/components/baseImage/BaseImage';

const LogoCardImage: React.FC<LogoCardImageProps> = React.memo(
  ({
    src,
    alt = 'Logo',
    width = 600,
    height = 400,
    title,
    description,
    link,
    category,
    copyBtn = false,
    copyBtnValue,
    downloadLinks,
    className,
    ...rest
  }) => {
    const hasOnlyImage = src && !title && !description;
    const [copied, setCopied] = useState(false);
    const renamedProps = renameDomProps(rest);
    const downloadFile = (url: string, filename: string) => {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename; // Force file name here
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    return (
      <div
        className={clsx(
          'logo-card-image group relative mx-auto flex min-h-[20em] max-w-[20em] flex-col overflow-hidden rounded-xl border border-neutral-25 bg-white p-10 text-brand-700 transition-all duration-300 sm:mx-0',
          {
            '!bg-neutral-25':
              hasOnlyImage ||
              (Array.isArray(category)
                ? category.includes('bgNeutral50')
                : category === 'bgNeutral50'),
            '!bg-brand-700': Array.isArray(category)
              ? category.includes('darkBg')
              : category === 'darkBg',
            '!p-0': Array.isArray(category)
              ? category.includes('noPadding')
              : category === 'noPadding',
          },
          className,
        )}
        {...renamedProps}
      >
        {src && (
          <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden">
            <BaseImage
              image={{ url: src }}
              width={`${height}`}
              height={`${height}`}
              alt={alt}
              classNameImage={clsx(
                'object-contain',
                Array.isArray(category)
                  ? category.includes('square')
                    ? 'mx-auto h-[101px] w-[101px]'
                    : 'h-full w-full'
                  : category === 'square'
                    ? 'mx-auto h-[101px] w-[101px]'
                    : 'h-full w-full',
                (Array.isArray(category) && category.includes('cover')) ||
                  category === 'cover'
                  ? '!object-cover'
                  : null,
              )}
            />
          </div>
        )}

        {title && description && (
          <div className="flex flex-col gap-4 self-stretch bg-white p-6">
            <h3 className="text-xl font-medium">{title}</h3>

            <div className="overflow-hidden">
              <div
                className="serialize-description m-0 text-base"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  height: '76px',
                  fontSize: '1.3rem',
                  background: 'white',
                }}
              >
                <SerializeDescription description={description} />
              </div>
            </div>

            {link && <Link {...link}>{link.title}</Link>}
          </div>
        )}

        {/* ✅ Hover Buttons */}
        {copyBtn ? (
          <div className="absolute bottom-0 left-0 right-0 z-10 flex justify-center p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <Button
              className="!h-fit border-[1px] border-brand-400 !px-4 !py-1 text-brand-700"
              name={copied ? 'Copied' : 'Copy'}
              variant="secondary"
              rightIcon={{ name: copied ? 'check' : 'copy', color: '#07314C' }}
              onClick={() => {
                if (copyBtnValue) {
                  navigator.clipboard.writeText(copyBtnValue).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  });
                }
              }}
            />
          </div>
        ) : (
          downloadLinks && (
            <div className="absolute bottom-0 left-0 right-0 z-10 flex justify-center gap-3 p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              {downloadLinks.eps && (
                <Button
                  className="!h-fit border-[1px] border-brand-400 !px-3 !py-1 text-brand-700"
                  name="EPS"
                  variant="secondary"
                  rightIcon={{ name: 'download', color: '#07314C' }}
                  onClick={() =>
                    downloadFile(
                      downloadLinks.eps!,
                      title ? `${title}.eps` : 'file.eps',
                    )
                  }
                />
              )}
              {downloadLinks.svg && (
                <Button
                  className="!h-fit border-[1px] border-brand-400 !px-3 !py-1 text-brand-700"
                  name="SVG"
                  variant="secondary"
                  rightIcon={{ name: 'download', color: '#07314C' }}
                  onClick={() =>
                    downloadFile(
                      downloadLinks.svg!,
                      title ? `${title}.svg` : 'file.svg',
                    )
                  }
                />
              )}
              {downloadLinks.png && (
                <Button
                  className="!h-fit border-[1px] border-brand-400 !px-3 !py-1 text-brand-700"
                  name="PNG"
                  variant="secondary"
                  rightIcon={{ name: 'download', color: '#07314C' }}
                  onClick={() =>
                    downloadFile(
                      downloadLinks.png!,
                      title ? `${title}.png` : 'file.png',
                    )
                  }
                />
              )}
            </div>
          )
        )}
      </div>
    );
  },
);

LogoCardImage.displayName = 'LogoCardImage';

export default LogoCardImage;
